(() => {
  'use strict';

  const config = window.surchargeCarbureConfig || {};
  const surchargeVariantId = Number(config.variantId);

  if (!Number.isInteger(surchargeVariantId) || surchargeVariantId <= 0) return;

  const rawFetch = window.fetch.bind(window);
  const cartMutationPattern = /\/cart\/(?:add|change|update|clear)\.js(?:\?|$)/;
  let debounceTimer;
  let totalsCorrectionTimer;
  let syncPromise = null;
  let syncAgain = false;
  let pendingCartMutations = 0;
  let lastSynchronizedCart = null;
  const observedTotalElements = new WeakSet();

  const waitForPendingCartMutations = async (timeout = 5000) => {
    const startedAt = Date.now();

    while (pendingCartMutations > 0 && Date.now() - startedAt < timeout) {
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
  };

  const request = async (url, options = {}) => {
    const response = await rawFetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {})
      }
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(`Surcharge Carbure (${response.status}) : ${message}`);
    }

    return response;
  };

  const getCart = async () => (await request(config.cartJsonUrl, { cache: 'no-store' })).json();

  const getSurchargeByVariant = async () => {
    const response = await rawFetch(config.metadataUrl, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'text/html' }
    });

    if (!response.ok) throw new Error(`Métadonnées panier indisponibles (${response.status})`);

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const surchargeByVariant = new Map();

    documentFragment.querySelectorAll('[data-surcharge-cart-line]').forEach((line) => {
      const variantId = Number(line.dataset.variantId);
      const unitCents = Number(line.dataset.surchargeUnitCents);

      if (Number.isInteger(variantId) && Number.isFinite(unitCents)) {
        surchargeByVariant.set(variantId, Math.max(0, Math.round(unitCents)));
      }
    });

    return surchargeByVariant;
  };

  const calculateSurchargeCents = (cart, surchargeByVariant) => cart.items.reduce((total, item) => {
    const variantId = Number(item.variant_id || item.id);
    if (variantId === surchargeVariantId) return total;

    const unitCents = surchargeByVariant.get(variantId) || 0;
    return total + (Math.max(0, Number(item.quantity) || 0) * unitCents);
  }, 0);

  const changeLine = (key, quantity) => request(config.cartChangeUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity })
  });

  const addSurchargeLine = (quantity) => request(config.cartAddUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [{
        id: surchargeVariantId,
        quantity,
        properties: { _surcharge_carbure: 'true' }
      }]
    })
  });

  const reconcileCart = async (cart, desiredQuantity) => {
    const surchargeLines = cart.items.filter((item) => Number(item.variant_id || item.id) === surchargeVariantId);

    if (desiredQuantity === 0) {
      for (const line of surchargeLines) await changeLine(line.key, 0);
      return surchargeLines.length > 0;
    }

    if (surchargeLines.length === 0) {
      await addSurchargeLine(desiredQuantity);
      return true;
    }

    const [keptLine, ...duplicateLines] = surchargeLines;
    for (const line of duplicateLines) await changeLine(line.key, 0);

    if (Number(keptLine.quantity) !== desiredQuantity) {
      await changeLine(keptLine.key, desiredQuantity);
    }

    return duplicateLines.length > 0 || Number(keptLine.quantity) !== desiredQuantity;
  };

  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.money_format) {
      return window.Shopify.formatMoney(cents, window.money_format);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
      style: 'currency',
      currency: config.currency || 'EUR'
    }).format(cents / 100);
  };

  const updateMoneyHtml = (element, cents) => {
    const moneyHtml = formatMoney(cents);

    if (element.dataset.surchargeCarbureCents === String(cents) && element.innerHTML.trim() === moneyHtml.trim()) {
      return;
    }

    element.dataset.surchargeCarbureCents = String(cents);
    element.innerHTML = moneyHtml;
  };

  const getLinePriceCents = (item) => {
    const finalLinePrice = Number(item.final_line_price);
    if (Number.isFinite(finalLinePrice)) return finalLinePrice;

    const linePrice = Number(item.line_price);
    if (Number.isFinite(linePrice)) return linePrice;

    const finalPrice = Number(item.final_price);
    if (Number.isFinite(finalPrice)) return finalPrice * (Number(item.quantity) || 0);

    const price = Number(item.price);
    if (Number.isFinite(price)) return price * (Number(item.quantity) || 0);

    return 0;
  };

  const getDisplayedSubtotalCents = (cart) => cart.items.reduce((total, item) => {
    return total + getLinePriceCents(item);
  }, 0);

  const updateDisplayedTotals = (cart) => {
    const displayedSubtotal = getDisplayedSubtotalCents(cart);
    const visibleItemCount = cart.items.reduce((total, item) => {
      return Number(item.variant_id || item.id) === surchargeVariantId
        ? total
        : total + Number(item.quantity || 0);
    }, 0);

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = visibleItemCount;
    });
    document.querySelectorAll('.cart-total-subtotal .cart-total-value .text').forEach((element) => {
      updateMoneyHtml(element, displayedSubtotal);
    });
    document.querySelectorAll('.previewCartTotals.subTotal .previewCartTotals-value').forEach((element) => {
      updateMoneyHtml(element, displayedSubtotal);
    });

    observeDisplayedTotals();
  };

  const scheduleDisplayedTotalsCorrection = (cart) => {
    lastSynchronizedCart = cart;
    updateDisplayedTotals(cart);

    window.clearTimeout(totalsCorrectionTimer);
    [150, 500, 1200].forEach((delay) => {
      window.setTimeout(() => {
        if (lastSynchronizedCart === cart) updateDisplayedTotals(cart);
      }, delay);
    });
  };

  const observeDisplayedTotals = () => {
    document.querySelectorAll('.cart-total-subtotal .cart-total-value .text, .previewCartTotals.subTotal .previewCartTotals-value').forEach((element) => {
      if (observedTotalElements.has(element)) return;

      const observer = new MutationObserver(() => {
        if (!lastSynchronizedCart) return;

        window.clearTimeout(totalsCorrectionTimer);
        totalsCorrectionTimer = window.setTimeout(() => {
          updateDisplayedTotals(lastSynchronizedCart);
        }, 0);
      });

      observedTotalElements.add(element);
      observer.observe(element, {
        childList: true,
        subtree: true,
        characterData: true
      });
    });
  };

  const updateInitialCartCount = () => {
    const lines = Array.from(document.querySelectorAll('[data-surcharge-cart-line]'));
    if (lines.length === 0) return;

    const visibleItemCount = lines.reduce((total, line) => {
      return Number(line.dataset.variantId) === surchargeVariantId
        ? total
        : total + Number(line.dataset.quantity || 0);
    }, 0);

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = visibleItemCount;
    });
  };

  const refreshVisibleCart = (cart, surchargeCents) => {
    scheduleDisplayedTotalsCorrection(cart);

    document.dispatchEvent(new CustomEvent('surcharge-carbure:updated', {
      detail: { cart, surchargeCents }
    }));
  };

  const synchronize = async () => {
    if (syncPromise) {
      syncAgain = true;
      return syncPromise;
    }

    syncPromise = (async () => {
      let synchronizedCart;
      let synchronizedSurchargeCents = 0;

      await waitForPendingCartMutations();

      do {
        syncAgain = false;
        await waitForPendingCartMutations();
        const surchargeByVariant = await getSurchargeByVariant();
        let cart = await getCart();
        const surchargeCents = calculateSurchargeCents(cart, surchargeByVariant);

        for (let attempt = 0; attempt < 3; attempt += 1) {
          const changed = await reconcileCart(cart, surchargeCents);
          if (!changed) break;
          cart = await getCart();
        }

        cart = await getCart();

        const synchronizedQuantity = cart.items.reduce((total, item) => {
          return Number(item.variant_id || item.id) === surchargeVariantId
            ? total + Number(item.quantity || 0)
            : total;
        }, 0);

        if (synchronizedQuantity !== surchargeCents) {
          throw new Error(`Quantité de surcharge incorrecte : ${synchronizedQuantity}/${surchargeCents}`);
        }

        refreshVisibleCart(cart, surchargeCents);
        synchronizedCart = cart;
        synchronizedSurchargeCents = surchargeCents;
      } while (syncAgain);

      return {
        cart: synchronizedCart,
        surchargeCents: synchronizedSurchargeCents
      };
    })().catch((error) => {
      console.error('[Surcharge Carbure]', error);
      return null;
    }).finally(() => {
      syncPromise = null;
    });

    return syncPromise;
  };

  const scheduleSynchronization = (delay = 80) => {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(synchronize, delay);
  };

  // Filet de sécurité pour les composants quantité du thème. Le premier
  // recalcul suit normalement le callback Shopify dans theme.js ; le second
  // couvre les applications qui remplacent ce gestionnaire par leur propre AJAX.
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-cart-quantity]')) return;

    window.setTimeout(() => scheduleSynchronization(0), 300);
    window.setTimeout(() => scheduleSynchronization(0), 1000);
  });

  document.addEventListener('click', async (event) => {
    const checkoutButton = event.target.closest('[data-surcharge-checkout]');
    if (!checkoutButton || checkoutButton.disabled) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutButton.disabled = true;

    const result = await synchronize();
    if (!result?.cart) {
      checkoutButton.disabled = false;
      window.alert(config.labels?.checkoutError || 'La Surcharge Carbure ne peut pas être synchronisée.');
      return;
    }

    window.location.assign(checkoutButton.dataset.checkoutUrl || config.checkoutUrl || '/checkout');
  });

  window.fetch = function surchargeAwareFetch(input, options) {
    const url = typeof input === 'string' ? input : input && input.url;
    const isCartMutation = url && cartMutationPattern.test(new URL(url, window.location.origin).pathname);

    if (isCartMutation) pendingCartMutations += 1;
    const promise = rawFetch(input, options);

    if (isCartMutation) {
      promise.finally(() => {
        pendingCartMutations = Math.max(0, pendingCartMutations - 1);
        scheduleSynchronization();
      }).catch(() => {});
    }

    return promise;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function surchargeAwareOpen(method, url, ...args) {
    this.surchargeCarbureUrl = url;
    return xhrOpen.call(this, method, url, ...args);
  };

  XMLHttpRequest.prototype.send = function surchargeAwareSend(...args) {
    const isCartMutation = this.surchargeCarbureUrl && cartMutationPattern.test(new URL(this.surchargeCarbureUrl, window.location.origin).pathname);

    if (isCartMutation) {
      pendingCartMutations += 1;
      this.addEventListener('loadend', () => {
        pendingCartMutations = Math.max(0, pendingCartMutations - 1);
        scheduleSynchronization();
      }, { once: true });
    }
    return xhrSend.apply(this, args);
  };

  window.SurchargeCarbure = { synchronize, schedule: scheduleSynchronization };
  updateInitialCartCount();
  observeDisplayedTotals();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleSynchronization(0), { once: true });
  } else {
    scheduleSynchronization(0);
  }
})();
